import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { PutJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, REST_API } from '@awp/constants';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { getAvalancheGeographicAreas, selectGeographicAreaLinesDd } from '@awp/stores/avalancheSlice';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import {
  AVALANCHE_PATH_PARAMS,
  AVALANCHE_PATHS_DEFAULT_DTO,
  AvalanchePathsService,
} from '@components/AvalanchePathsAdmin/AvalanchePathsService';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { AwpContainer } from '@components/Common/AwpContainer';
import { getListPageParams } from '@awp/helpers/navigation';

const AvalanchePathsEdit = (props) => {
  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheList = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const areaLinesRaw = useSelector((state) => selectGeographicAreaLinesDd(state));

  const avalanchePrograms = programAreas;

  const navigate = useNavigate();
  const { id: entityId } = useParams();
  const listPageURL = getListPageParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [area, setArea] = useState({});
  const [areaList, setAreaList] = useState(avalancheList);
  const [entityErrors, setEntityErrors] = useState(null);
  const [areaId, setAreaId] = useState(null);
  const [entityData, setEntityData] = useState({});
  const [loading, setLoading] = useState(false);
  const [areaLines, setAreaLines] = useState(
    areaLinesRaw.filter((x) => x.geographicPointType === 'ROAD CLOSURE POINT'),
  );
  const [tabKey, setTabKey] = useState(searchParams.get(AVALANCHE_PATH_PARAMS.TAB_KEY) ?? undefined);

  const avalanchePathsService = new AvalanchePathsService(FORM_MODE.EDIT);
  const { readOnly, title, relativePath, successMessage, mode, entityName } = avalanchePathsService.crud;

  useEffect(() => {
    const avalancheProgram = avalanchePrograms.find((x) => x.value === area.parentId);
    if (avalancheProgram) {
      const areaListFilterCriteria = (x) => x.parentId === avalancheProgram.value;
      setAreaList(avalancheList.filter(areaListFilterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
    }
    loadAvalanchePath();
  }, [entityId]);

  const onNameInput = (name, setFieldValue) => {
    entityData.Name = name;
    setEntityData(entityData);
    setFieldValue('Name', entityData.Name, false);
  };

  const loadAvalanchePath = async () => {
    setLoading(true);

    return AvalanchePathsService.loadAvalanchePath(entityId).then(async (record) => {
      if (isValueEmpty(record)) {
        setEntityData({});
      } else {
        setEntityData({
          metaFields: AVALANCHE_PATHS_DEFAULT_DTO.metaFields,
          ...record,
        });
      }

      setLoading(false);
    });
  };

  const updateAvalanchePath = (setSubmitting, resetForm, setErrors, avalanchePathDto) => {
    const url = `${REST_API.AVALANCHE_PATH}${entityId}`;
    PutJson(url, avalanchePathDto)
      .then(async (response) => {
        if (response.ok) {
          const responseData = await response.json();
          updateData({});
          resetForm({
            values: {
              ...avalanchePathsService.mapInitialValues({
                entityData: responseData,
                avalanchePrograms,
                areaList,
                areaId,
              }),
            },
          });
          toast.success(successMessage);
        } else if (response.status === 412) {
          const errors = await response.json();
          resetForm({
            values: {
              ...avalanchePathsService.mapInitialValues({
                entityData: avalanchePathDto,
                avalanchePrograms,
                areaList,
                areaId,
              }),
            },
          });
          setErrors(errors);
          if (errors.entity) setEntityErrors({ ['Avalanche Path']: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updateData = (data) => {
    const index = areaLines.findIndex((x) => x.value === entityData.value);
    if (index < 0) return;
    areaLines[index] = { ...entityData };
    setAreaLines([...areaLines]);
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    entityData.name = values.name;
    entityData.isDisabled = !values.active;
    entityData.code = values.code;
    entityData.locationNote = values.locationNote;
    entityData.pathNumber = values.pathNumber;
    entityData.status = values.active;

    const avalanchePathDto = {
      ...entityData,
      ...avalanchePathsService.mapFormToDTO({ entityData: values, areaId }),
    };

    updateAvalanchePath(setSubmitting, resetForm, setErrors, avalanchePathDto);
  };

  const onTabSelect = (event) => {
    setTabKey(event);

    avalanchePathsService.setTabSearchParams(setSearchParams, event);
  };

  const onClickCancel = () => {
    navigate(`${relativePath}${listPageURL}`);
  };

  return (
    <>
      <AwpContainer>
        {loading ? (
          <Container>
            <Spinner animation="border" />
          </Container>
        ) : (
          <>
            <Formik
              enableReinitialize
              validateOnMount={true}
              initialValues={{
                ...avalanchePathsService.mapInitialValues({ entityData, avalanchePrograms, areaList, areaId }),
              }}
              validationSchema={AvalanchePathsService.getValidations()}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <Form>
                  <Link className="awp-text-btn" to={`${relativePath}${listPageURL}`}>
                    {`List ${entityName}`}
                  </Link>
                  <span className="mx-3">&gt;</span>
                  <span>{title}</span>
                  <Card border="light" className="page-card">
                    <Card.Header>
                      <Row className="mt-4"></Row>
                      {avalanchePathsService.crud.getHeader()}
                    </Card.Header>
                    <Card.Body>
                      {avalanchePathsService.getFields({
                        entityData,
                        readOnly,
                        avalanchePrograms,
                        areaList,
                        onNameInput,
                        loading,
                        props,
                        mode,
                        onTabSelect,
                      })}
                    </Card.Body>
                    <Card.Footer>
                      <AwpCloseCancelButtons
                        props={props}
                        isPristine={!props.dirty || props.isSubmitting}
                        hasAccess={true}
                        onConfirmedCancel={() => {
                          onClickCancel();
                        }}
                      />
                    </Card.Footer>
                  </Card>
                </Form>
              )}
            </Formik>
            <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
          </>
        )}
      </AwpContainer>
    </>
  );
};

export default AvalanchePathsEdit;
